import { Fragment, useEffect, useState, useRef, useCallback } from "react";
import "react-tooltip/dist/react-tooltip.css";
import { ReactSVG } from "react-svg";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { XMarkIcon } from "@heroicons/react/24/outline";
import { Transition } from "@headlessui/react";

import Alert from "../common/Alert";

import {
  calculateAge,
  formatHealth,
  formatToYYYY_MMM_DD,
  formatToYYYYMMDDBackend,
  extractPhoneNumber,
} from "../../helpers/functions";
import {
  genderOptions,
  insurancePlanOptions,
  weightDropdownOptions,
  heightDropdownOptions,
} from "./create/options";
import MedicalConditionListPopup from "./MedicalConditionListPopup";
import ClinicalAlertListPopup from "./ClinicalAlertListPopup";
import ListPopup from "../common/ListPopup";
import CustomInput from "../common/CustomInput";
import Button from "../common/Button";
import NewToolTip from "../common/New-ToolTip";
import PrimaryPhysician from "../common/tooltip/primary-physician";
import AllergiesToolTip from "../common/tooltip/allergies";
import MedicalCondition from "../common/tooltip/medical-condition";
import UncodedAllergy from "../common/tooltip/patient/uncoded-allergy";
import { severityOptions } from "../patient/clinical-services/service-steps/clinical-assessment/data";
import {
  updatePatientWeight,
  updatePatientHeight,
  updatePatientProvincialInsuranceDetails,
  createPatientProvincialInsuranceDetails,
  fetchClinicalAlerts,
} from "../../redux/modules/patient/actions";
import {
  isHealthCareError,
  isVersionCodeError,
  isValidExpiryDate,
  formatEnCA,
  toDate,
} from "../../helpers/input-validations";
import {
  capitalizeFirstLetter,
  convertWeight,
  convertHeight,
  formatDateToDD_MMM_YYYY,
} from "../../helpers/functions";
import ConfirmationModalNew from "../common/ConfirmationModalNew";
import ChartUpdatesDialog from "./create/ChartUpdatesDialog";

const formatLastUpdatedDate = (d) => {
  if (d == null || d === "" || new Date(d).getTime() === 0) return "-";
  return formatDateToDD_MMM_YYYY(d);
};

const getTodayDateString = () => new Date().toISOString().split("T")[0];

const Overview = ({ patient, isFormViewer = false }) => {
  const getValidExpiryDate = (value) => {
    const date = toDate(value);
    if (!date || date.getUTCFullYear() < 1900) return "";
    return formatEnCA(date);
  };
  const healthCardExpiry = getValidExpiryDate(
    patient?.provincialInsurance?.expiryDate
  );
  const healthCardExpiryDateObj = toDate(healthCardExpiry);
  const isHealthCardExpired = healthCardExpiry
    ? !isValidExpiryDate(healthCardExpiry)
    : false;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const { medicalConditions: condition } = useSelector(
    (state) => state.clinicalService
  );
  const { medicationReviews, minorAilments } = useSelector(
    (state) => state.clinicalService.clinicalServicesSmall
  );
  const { allergy } = useSelector((state) => state.patients);
  const rawClinicalAlerts = useSelector(
    (state) => state.patients?.clinicalAlerts?.clinicalAlerts
  );
  const clinicalAlerts = Array.isArray(rawClinicalAlerts) ? rawClinicalAlerts : [];
  const { medicalConditions } = condition;
  const [openList, setOpenList] = useState(false);
  const [openMedicalConditionList, setOpenMedicalConditionList] =
    useState(false);
  const [showInactivePatientAlert, setInactivePatientAlert] = useState(true);
  const [isShowingWeightForm, setIsShowingWeightForm] = useState(false);

  const [isShowingHeightForm, setIsShowingHeightForm] = useState(false);
  const [isShowingProvincialForm, setIsShowingProvincialForm] = useState(false);
  const [healthCardNumberInput, setHealthCardNumberInput] = useState(
    patient?.provincialInsurance?.cardNumber ?? ""
  );
  const [healthCardVersionInput, setHealthCardVersionInput] = useState(
    patient?.provincialInsurance?.versionCode?.toUpperCase() ?? ""
  );
  const [healthCardExpiryInput, setHealthCardExpiryInput] = useState(
    getValidExpiryDate(patient?.provincialInsurance?.expiryDate)
  );
  const [healthCardNumberError, setHealthCardNumberError] = useState(false);
  const [healthCardVersionError, setHealthCardVersionError] = useState(false);
  const [healthCardExpiryError, setHealthCardExpiryError] = useState(false);

  const primaryPhysician =
    patient?.careProvider?.find((item) => item?.primaryProvider) ||
    patient?.careProvider?.[0] ||
    null;
  const [showUnCodedTooltip, setShowUnCodedTooltip] = useState(false);
  const [measuredWeight, setMeasuredWeight] = useState(null);
  const [measuredWeightUnit, setMeasuredWeightUnit] = useState(null);
  const [unitError, setUnitError] = useState(false);
  const [estimatedUnitError, setEstimatedUnitError] = useState(false);
  const [estimatedWeight, setEstimatedWeight] = useState(null);
  const [estimatedWeightUnit, setEstimatedWeightUnit] = useState(null);
  const [updatedWeightDate, setUpdatedWeightDate] = useState("");
  const [disableMeasuredWeight, setDisableMeasuredWeight] = useState(false);
  const [disableEstimatedWeight, setDisableEstimatedWeight] = useState(false);

  const [measuredHeight, setMeasuredHeight] = useState();
  const [measuredHeightUnit, setMeasuredHeightUnit] = useState();
  const [heightUnitError, setHeightUnitError] = useState(false);
  const [updatedHeightDate, setUpdatedHeightDate] = useState("");
  const [openExitServiceDialog, setOpenExitServiceDialog] = useState(false);
  const [openClinicalAlertList, setOpenClinicalAlertList] = useState(false);

  // Fetch clinical alerts per patient to ensure patient-specific list
  useEffect(() => {
    if (patient?.id) {
      dispatch(fetchClinicalAlerts(patient.id));
    }
  }, [patient?.id]);

  const resetProvincialForm = useCallback(() => {
    const insurance = patient?.provincialInsurance;
    setHealthCardNumberInput(insurance?.cardNumber ?? "");
    setHealthCardVersionInput(insurance?.versionCode?.toUpperCase() ?? "");
    setHealthCardExpiryInput(getValidExpiryDate(insurance?.expiryDate));
    setHealthCardNumberError(false);
    setHealthCardVersionError(false);
    setHealthCardExpiryError(false);
  }, [patient?.provincialInsurance]);

  useEffect(() => {
    resetProvincialForm();
  }, [resetProvincialForm]);

  useEffect(() => {
    if (measuredWeight > 0 && measuredWeight) {
      setEstimatedUnitError(false);
      setEstimatedWeight(null);
      setEstimatedWeightUnit(null);
      setDisableEstimatedWeight(true);
    } else if (estimatedWeight > 0 && estimatedWeight) {
      setUnitError(false);
      setMeasuredWeight(null);
      setMeasuredWeightUnit(null);
      setDisableMeasuredWeight(true);
    } else {
      setEstimatedUnitError(false);
      setEstimatedWeight(null);
      setEstimatedWeightUnit(null);
      setDisableEstimatedWeight(false);
      setUnitError(false);
      setMeasuredWeight(null);
      setMeasuredWeightUnit(null);
      setDisableMeasuredWeight(false);
    }
  }, [measuredWeight, estimatedWeight]);

  const initializeWeightForm = () => {
    setMeasuredWeight(null);
    setMeasuredWeightUnit(null);
    setEstimatedWeight(null);
    setEstimatedWeightUnit(null);
    setUpdatedWeightDate("");

    setDisableMeasuredWeight(false);
    setDisableEstimatedWeight(false);
  };

  const initializeHeightForm = () => {
    setMeasuredHeight(null);
    setMeasuredHeightUnit(null);
    setUpdatedHeightDate("");
  };

  // Auto-fill Date Updated when Measured Height or Unit is set; clear when both are empty
  useEffect(() => {
    if (!isShowingHeightForm) return;
    const hasHeight = measuredHeight != null && String(measuredHeight).trim() !== "";
    const hasUnit = measuredHeightUnit != null && measuredHeightUnit !== "";
    setUpdatedHeightDate(hasHeight || hasUnit ? getTodayDateString() : "");
  }, [isShowingHeightForm, measuredHeight, measuredHeightUnit]);

  // Auto-fill Date Updated when measured or estimated weight + unit are set; clear when both pairs are empty
  useEffect(() => {
    if (!isShowingWeightForm) return;
    const hasMeasured = measuredWeight != null && String(measuredWeight).trim() !== "" && measuredWeightUnit != null && measuredWeightUnit !== "";
    const hasEstimated = estimatedWeight != null && String(estimatedWeight).trim() !== "" && estimatedWeightUnit != null && estimatedWeightUnit !== "";
    setUpdatedWeightDate(hasMeasured || hasEstimated ? getTodayDateString() : "");
  }, [isShowingWeightForm, measuredWeight, measuredWeightUnit, estimatedWeight, estimatedWeightUnit]);

  const checkAnyRunningService = () => {
    return (
      medicationReviews?.some((value) => value.status === "inProgress") ||
      minorAilments?.some((value) => value.status === "inProgress")
    );
  };

  const handleWeightUpdate = () => {
    setUnitError(false);
    setEstimatedUnitError(false);
    
    // Check if at least one weight value is provided
    if (!measuredWeight && !estimatedWeight) {
      // Optionally show an error message that at least one weight is required
      return;
    }
    
    if (measuredWeight && !measuredWeightUnit) setUnitError(true);
    else if (estimatedWeight && !estimatedWeightUnit)
      setEstimatedUnitError(true);
    else {
      const measuredInKg =
        measuredWeight != null && measuredWeight !== ""
          ? convertWeight(Number(measuredWeight), measuredWeightUnit || "kg", "kg")
          : null;
      const estimatedInKg =
        estimatedWeight != null && estimatedWeight !== ""
          ? convertWeight(Number(estimatedWeight), estimatedWeightUnit || "kg", "kg")
          : null;

      const request = {
        bodyWeight: measuredInKg,
        estimatedBodyWeight: estimatedInKg,
        bodyWeightUnit: measuredInKg != null ? "kg" : null,
        estimatedBodyWeightUnit: estimatedInKg != null ? "kg" : null,
        lastWeighedDate: updatedWeightDate,
      };
      dispatch(updatePatientWeight(patient?.id, request));
      weightFormDialog(false);
      resetForm();
    }
  };

  const handleProvincialFormOpen = () => {
    resetProvincialForm();
    setIsShowingProvincialForm(true);
    setIsShowingHeightForm(false);
    weightFormDialog(false);
  };

  const handleProvincialFormClose = () => {
    resetProvincialForm();
    setIsShowingProvincialForm(false);
  };

  const getProvinceValue = () =>
    patient?.provincialInsurance?.province ||
    patient?.address?.province ||
    "ontario";

  const validateProvincialInputs = () => {
    const cardError = isHealthCareError(healthCardNumberInput);
    const versionError = isVersionCodeError(
      healthCardVersionInput?.toUpperCase()
    );
    const expiryError = healthCardExpiryInput
      ? !isValidExpiryDate(healthCardExpiryInput)
      : false;

    setHealthCardNumberError(cardError);
    setHealthCardVersionError(versionError);
    setHealthCardExpiryError(expiryError);

    return !(cardError || versionError || expiryError);
  };

  const buildProvincialPayload = () => ({
    cardNumber: extractPhoneNumber(healthCardNumberInput),
    versionCode: healthCardVersionInput?.toUpperCase() ?? "",
    expiryDate: healthCardExpiryInput
      ? formatToYYYYMMDDBackend(healthCardExpiryInput)
      : null,
    province: getProvinceValue(),
  });

  const handleProvincialInsuranceUpdate = () => {
    if (!validateProvincialInputs()) {
      return;
    }

    const payload = buildProvincialPayload();

    if (patient?.provincialInsurance?.id) {
      dispatch(
        updatePatientProvincialInsuranceDetails(
          patient.provincialInsurance.id,
          payload
        )
      );
    } else if (patient?.id) {
      dispatch(
        createPatientProvincialInsuranceDetails(patient.id, payload)
      );
    }

    setIsShowingProvincialForm(false);
  };

  const resetForm = () => {
    setMeasuredHeight(null);
    setMeasuredHeightUnit(null);
    setHeightUnitError(false);
    setEstimatedUnitError(false);
    setEstimatedWeight(null);
    setEstimatedWeightUnit(null);
    setDisableEstimatedWeight(false);
    setUnitError(false);
    setMeasuredWeight(null);
    setMeasuredWeightUnit(null);
    setDisableMeasuredWeight(false);
  };

  const handleHeightUpdate = () => {
    setHeightUnitError(false);
    
    // Check if height value is provided
    if (!measuredHeight) {
      return;
    }
    
    if (measuredHeight && !measuredHeightUnit) {
      setHeightUnitError(true);
      return;
    }
    const heightInCm =
      measuredHeight != null && measuredHeight !== ""
        ? convertHeight(Number(measuredHeight), measuredHeightUnit || "cm", "cm")
        : null;

    const request = {
      bodyHeight: heightInCm,
      bodyHeightUnit: heightInCm != null ? "cm" : null,
      lastHeighedDate: updatedHeightDate,
    };
    dispatch(updatePatientHeight(patient?.id, request));
    heightFormDialog(false);
  };

  const handleClosePatient = () => {
    // medicationReviews.forEach((value) => {
    //   if (value.status === "inProgress")
    //     dispatch(updateMedicalReviewStatus(value.id, "deleted"));
    // });
    // minorAilments.forEach((value) => {
    //   if (value.status === "inProgress")
    //     dispatch(updateMinorAilmentStatus(value.id, "deleted"));
    // });
    navigate("/patients");
  };

  const showBodyWeightAlert = () => {
    const lastWeightDate = new Date(patient?.bodyWeight?.lastWeighedDate);
    const today = new Date();
    const oneYearInMillis = 365 * 24 * 60 * 60 * 1000;
    return Math.abs(today - lastWeightDate) > oneYearInMillis;
  };

  const weightFormDialog = (show = false) => {
    if (!show) {
      resetForm();
      setIsShowingWeightForm(false);
      return;
    }
    initializeWeightForm();
    setIsShowingWeightForm(true);
  };

  const heightFormDialog = (show = false) => {
    if (!show) {
      resetForm();
      setIsShowingHeightForm(false);
      return;
    }
    initializeHeightForm();
    setIsShowingHeightForm(true);
  };

  const formatPatientWeightKg = () => {
    const bw = patient?.bodyWeight;
    const value = bw?.bodyWeight ?? bw?.estimatedBodyWeight;
    const unit = bw?.bodyWeightUnit ?? bw?.estimatedBodyWeightUnit;
    if (value == null || value === "") return "Not Available";
    const inKg = unit && unit !== "kg" ? convertWeight(Number(value), unit, "kg") : Number(value);
    return `${inKg} kg`;
  };

  const formatPatientHeightCm = () => {
    const bh = patient?.bodyHeight;
    const value = bh?.bodyHeight;
    const unit = bh?.bodyHeightUnit;
    if (value == null || value === "") return "Not Available";
    const inCm = unit && unit !== "cm" ? convertHeight(Number(value), unit, "cm") : Number(value);
    return `${inCm} cm`;
  };

  const WeightForm = () => {
    const formRef = useRef(null);

    // Close the form when clicking outside of it
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (formRef.current && !formRef.current.contains(event.target)) {
          weightFormDialog(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [formRef]);

    return (
      <div className="relative flex gap-2">
        <p
          onClick={() => {
            weightFormDialog(true);
            setIsShowingHeightForm(false);
          }}
          className="flex gap-2"
        >
          <span className="underline text-gray-700 cursor-pointer">
            Weight :
          </span>
          <strong className="text-gray-800">{formatPatientWeightKg()}</strong>
        </p>

        {showBodyWeightAlert() && (
          <ReactSVG src="/icons/exclude.svg" className="w-[12px]" />
        )}

        <Transition
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
          show={isShowingWeightForm}
        >
          <div
            ref={formRef}
            className="absolute z-10 w-screen max-w-sm px-4 pt-2 transform -translate-x-1/2 top-full left-1/2 sm:px-0"
          >
            <div className="overflow-hidden bg-white border-2 border-gray-100 rounded shadow-lg">
              <div className="flex items-center gap-4 p-4">
                {showBodyWeightAlert() && (
                  <ReactSVG src="/icons/exclude.svg" className="w-[12px]" />
                )}
                {patient?.bodyWeight != null && (
                  <p className="text-teal-600">
                    Weight Last Updated:{" "}
                    {formatLastUpdatedDate(patient?.bodyWeight?.lastWeighedDate)}
                  </p>
                )}
              </div>
              {/* Form fields */}
              <div className="flex flex-col gap-3 p-4">
                {/* Your form inputs go here */}
              </div>
            </div>
          </div>
        </Transition>
      </div>
    );
  };

  const showMedicalConditions = () => {
    const isMinorAilmentRunning = minorAilments?.some((item) => item.status === "inProgress");
    if (isMinorAilmentRunning) {
      setOpenExitServiceDialog(true);
    } else {
      setOpenMedicalConditionList(true);
    }
  };
  
  return (
    <div
      style={
        isFormViewer
          ? {}
          : {
              background: "url(/images/overview-image.png)",
              backgroundSize: "cover",
            }
      }
      className="p-5"
    >
      {patient?.status !== "active" && showInactivePatientAlert ? (
        <Alert
          title="Inactive Patient"
          type="danger"
          className="mb-4"
          content={() =>
            `Switched Pharmacy on ${formatToYYYY_MMM_DD("2023-05-06")}.`
          }
          handleClose={() => setInactivePatientAlert(false)}
        />
      ) : null}
      <div className="relative flex items-center gap-4 p-5 text-gray-700 bg-white divide-x divide-gray-100 rounded text-body-sm 2xl:gap-10 ">
        <div className="2xl:min-w-[24%] max-w-[20%] min-h-[80px] 2xl:flex flex-col gap-2 bg-gray-20 p-2.5 rounded flex justify-center">
          <h5 className="text-xs-display font-bold text-teal-300 ">
            {" "}
            {capitalizeFirstLetter(patient?.lastName)},{" "}
            {capitalizeFirstLetter(patient?.firstName)}{" "}
            {capitalizeFirstLetter(patient?.middleName)}{" "}
          </h5>
          <div className="flex justify-start gap-2.5">
            {patient?.isPregnant && (
              <div>
                <ReactSVG
                  src="/icons/pregnancy.svg"
                  className="cursor-pointer w-7 2xl:w-8"
                  data-tooltip-id="pregnancy"
                />
                <NewToolTip
                  id="pregnancy"
                  render={() => (
                    <div className="!bg-white max-w-[460px] text-teal-300 !font-normal text-body-sm p-2">
                      Pregnancy
                    </div>
                  )}
                />
              </div>
            )}
            {patient?.isBreastFeeding && (
              <div>
                <ReactSVG
                  src="/icons/breast-feeding.svg"
                  className="cursor-pointer w-7 2xl:w-8"
                  data-tooltip-id="breast-feeding"
                />
                <NewToolTip
                  id="breast-feeding"
                  render={() => (
                    <div className="!bg-white max-w-[460px] text-teal-300 !font-normal text-body-sm p-2">
                      Breast Feeding
                    </div>
                  )}
                />
              </div>
            )}

            {/* <ReactSVG
              src="/icons/blindness.svg"
              className="cursor-pointer w-7 2xl:w-8"
              data-tooltip-id="blindness"
            />
            <NewToolTip
              id="blindness"
              render={() => (
                <div className="!bg-white max-w-[460px] text-teal-300 !font-normal text-body-sm p-2">
                  Blindness
                </div>
              )}
            /> */}

            {patient?.isMedicationAdherence && (
              <div>
                <ReactSVG
                  src="/icons/late-medicine.svg"
                  className="cursor-pointer w-7 2xl:w-8"
                  data-tooltip-id="late-medicine"
                />
                <NewToolTip
                  id="late-medicine"
                  render={() => (
                    <div className="!bg-gray-10 border border-gray-100 max-w-[460px] text-teal-300 !font-normal text-body-sm p-2">
                      Medication Adherence - Non Compliance Identified
                      <br />
                      <span className="text-gray-600">How to Manage → Module Coming Soon</span>
                    </div>
                  )}
                />
              </div>
            )}
          </div>
        </div>
        <div className="space-y-2 2xl:pl-10 pl-4 2xl:min-w-[10%] cursor-default">
          <p className="flex gap-2">
            Age :
            <strong className="text-gray-800">
              {calculateAge(patient?.dateOfBirth)}
            </strong>
          </p>
          <p className="flex gap-2">
            Gender :
            <strong className="text-gray-800 capitalize">
              {genderOptions.find((g) => g.value === patient?.gender)?.label ||
                "Not Available"}
            </strong>
          </p>
          <p className="flex gap-2">
            DOB :
            <strong className="text-gray-800">
              {formatToYYYY_MMM_DD(patient?.dateOfBirth)}
            </strong>
          </p>
        </div>
        <div className="space-y-2 2xl:pl-10 pl-4 2xl:min-w-[10%] cursor-default">
          <div className="relative flex gap-2">
            <p
              onClick={() => {
                weightFormDialog(true);
                setIsShowingHeightForm(false);
              }}
              className="flex gap-2"
            >
              <span className="underline text-gray-700 cursor-pointer">
                Weight :
              </span>
              <strong className="text-gray-800">{formatPatientWeightKg()}</strong>
            </p>

            {showBodyWeightAlert() && (
              <ReactSVG src="/icons/exclude.svg" className="w-[12px] " />
            )}

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
              show={isShowingWeightForm}
            >
              <div className="absolute z-50 w-screen max-w-sm px-4 pt-2 transform -translate-x-1/2 top-full left-1/2 sm:px-0">
                <div className="overflow-hidden bg-white border-2 border-gray-100 rounded shadow-lg">
                  <div className="flex items-center gap-4 p-4">
                    {showBodyWeightAlert() && (
                      <ReactSVG
                        src="/icons/exclude.svg"
                        className="w-[12px] "
                      />
                    )}
                    {patient?.bodyWeight != null && (
                      <p className="text-teal-600">
                        Weight Last Updated:{" "}
                        {formatLastUpdatedDate(patient?.bodyWeight?.lastWeighedDate)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-3 p-4">
                    <div className="flex items-center absolute right-6 top-8">
                      <button
                        type="button"
                        className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                        onClick={() => {
                          weightFormDialog(false);
                        }}
                      >
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon
                          className="w-5 h-5 m-auto"
                          strokeWidth="2"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                    <h3 className="text-body-md font-bold text-teal-600">
                      Update Weight:
                    </h3>
                    <div className="flex flex-col gap-2.5">
                      <div className="flex items-center space-x-3">
                        <CustomInput
                          name="measuredWeight"
                          label="Measured Weight"
                          placeholder="Enter Measured Weight"
                          type="weight"
                          disabled={disableMeasuredWeight}
                          showOptionalLabel={false}
                          value={measuredWeight}
                          className="w-full max-w-[70%]"
                          onChange={(e) => setMeasuredWeight(e.target.value)}
                        />
                        <CustomInput
                          options={weightDropdownOptions}
                          name={`measuredWeightUnit`}
                          disabled={disableMeasuredWeight}
                          className="col-span-12 w-full max-w-[30%]"
                          label="Unit"
                          type="dropdown"
                          value={measuredWeightUnit}
                          onChange={(e) => {
                            setMeasuredWeight(
                              convertWeight(
                                measuredWeight,
                                measuredWeightUnit,
                                e.value
                              )
                            );
                            setMeasuredWeightUnit(e.value);
                          }}
                          error={unitError}
                          required
                        />
                      </div>
                      <div className="flex items-center space-x-3">
                        <CustomInput
                          name="estimatedWeight"
                          disabled={disableEstimatedWeight}
                          label="Estimated Weight"
                          placeholder="Enter Estimated Weight"
                          type="weight"
                          showOptionalLabel={false}
                          value={estimatedWeight}
                          className="w-full max-w-[70%]"
                          onChange={(e) => setEstimatedWeight(e.target.value)}
                        />
                        <CustomInput
                          options={weightDropdownOptions}
                          name={`estimatedWeightUnit`}
                          disabled={disableEstimatedWeight}
                          className="col-span-12 w-full max-w-[30%]"
                          label="Unit"
                          type="dropdown"
                          value={estimatedWeightUnit}
                          onChange={(e) => {
                            setEstimatedWeight(
                              convertWeight(
                                estimatedWeight,
                                estimatedWeightUnit,
                                e.value
                              )
                            );
                            setEstimatedWeightUnit(e.value);
                          }}
                          error={estimatedUnitError}
                          required
                        />
                      </div>
                      <CustomInput
                        name="dateUpdated"
                        label="Date Updated"
                        type="date"
                        value={updatedWeightDate}
                        required
                        onChange={(value) => setUpdatedWeightDate(value)}
                      />
                    </div>
                    <div className="flex items-center justify-center gap-5">
                      <Button
                        className="w-1/2 "
                        text={`Cancel`}
                        type="primary-outline"
                        onClick={() => {
                          weightFormDialog(false);
                          resetForm();
                        }}
                      />
                      <Button
                        className="w-1/2 "
                        text={
                          patient?.bodyWeight?.estimatedBodyWeight ||
                          patient?.bodyWeight?.bodyWeight
                            ? "Update"
                            : "Save"
                        }
                        type="primary"
                        onClick={() => {
                          handleWeightUpdate();
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Transition>
          </div>

          <div className="relative flex gap-2">
            <p
              onClick={() => {
                heightFormDialog(true);
                weightFormDialog(false);
              }}
              className="flex gap-2"
            >
              <span className="underline text-gray-700 cursor-pointer">
                Height :
              </span>
              <strong className="text-gray-800">{formatPatientHeightCm()}</strong>
            </p>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
              show={isShowingHeightForm}
            >
              <div className="absolute z-50 w-screen max-w-sm px-4 pt-2 transform -translate-x-1/2 top-full left-1/2 sm:px-0">
                <div className="overflow-hidden bg-white border-2 border-gray-100 rounded shadow-lg">
                  <div className="flex items-center gap-4 p-4">
                    {showBodyWeightAlert() && (
                      <ReactSVG
                        src="/icons/exclude.svg"
                        className="w-[12px] "
                      />
                    )}
                    {patient?.bodyHeight != null && (
                      <p className="text-teal-600">
                        Height Last Updated:{" "}
                        {(() => {
                          const d = patient?.bodyHeight?.lastHeighedDate;
                          if (d == null || d === "" || new Date(d).getTime() === 0) return "-";
                          return formatDateToDD_MMM_YYYY(d);
                        })()}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-3 p-4">
                    <div className="flex items-center absolute right-6 top-8">
                      <button
                        type="button"
                        className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                        onClick={() => {
                          heightFormDialog(false);
                        }}
                      >
                        <span className="sr-only">Close panel</span>
                        <XMarkIcon
                          className="w-5 h-5 m-auto"
                          strokeWidth="2"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                    <h3 className="text-body-md font-bold text-teal-600">
                      Update Height:
                    </h3>
                    <div className="flex flex-col gap-2.5">
                      <div className="flex items-center space-x-3">
                        <CustomInput
                          name="measuredHeight"
                          label="Measured Height"
                          placeholder="Enter Measured Height"
                          type="height"
                          showOptionalLabel={false}
                          value={measuredHeight}
                          className="w-full max-w-[70%]"
                          onChange={(e) => setMeasuredHeight(e.target.value)}
                        />
                        <CustomInput
                          options={heightDropdownOptions}
                          name={`measuredHeightUnit`}
                          className="col-span-12 w-full max-w-[30%]"
                          label="Unit"
                          type="dropdown"
                          value={measuredHeightUnit}
                          onChange={(e) => {
                            setMeasuredHeight(
                              convertHeight(
                                measuredHeight,
                                measuredHeightUnit,
                                e.value
                              )
                            );
                            setMeasuredHeightUnit(e.value);
                          }}
                          error={heightUnitError}
                          required
                        />
                      </div>
                      <CustomInput
                        name="dateUpdated"
                        label="Date Updated"
                        type="date"
                        value={updatedHeightDate}
                        required
                        onChange={(value) => setUpdatedHeightDate(value)}
                      />
                    </div>
                    <div className="flex items-center justify-center gap-5">
                      <Button
                        className="w-1/2 "
                        text={`Cancel`}
                        type="primary-outline"
                        onClick={() => {
                          heightFormDialog(false);
                        }}
                      />
                      <Button
                        className="w-1/2 "
                        text={
                          patient?.bodyHeight?.bodyHeight ? "Update" : "Save"
                        }
                        type="primary"
                        onClick={() => {
                          handleHeightUpdate();
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
          <p className="flex gap-2">
            BMI :<strong className="text-gray-800">{patient?.ibw || `18`}</strong>
          </p>
        </div>
        <div className="pl-4 space-y-2 2xl:pl-10 lg:w-[18%] whitespace-nowrap cursor-default">
          <p className="flex  items-center gap-2">
            Primary Provider :
            {primaryPhysician ? (
              <strong className="text-gray-800">
                Dr {capitalizeFirstLetter(primaryPhysician?.firstName?.[0] || "")}.
                &nbsp;
                {/* {primaryPhysician?.middleName != null ? 
                    primaryPhysician?.middleName 
                    : ""}   */}
                {capitalizeFirstLetter(primaryPhysician?.lastName)}
              </strong>
            ) : (
              <div className="relative flex gap-2 items-center">
                <strong className="text-gray-800"> None </strong>
                <ReactSVG
                  src="/icons/information-dark.svg"
                  className="w-3.5"
                  data-tooltip-id="primary-physician"
                />
                <NewToolTip
                  id="primary-physician"
                  render={() => <PrimaryPhysician />}
                />
              </div>
            )}
          </p>

          <div className="relative flex gap-2">
            <button
              type="button"
              onClick={handleProvincialFormOpen}
              className="flex items-center gap-2 text-left group"
            >
              <span className="underline text-gray-700 group-hover:text-teal-950">
                Health Card :
              </span>
              {patient?.provincialInsurance ? (
                <span
                  data-tooltip-id={isHealthCardExpired || !healthCardExpiry ? "health_card_tooltip" : undefined}
                  className={`font-bold ${
                    !(isHealthCardExpired || !healthCardExpiry) ? "text-gray-800" : "text-red-700"
                  }`}
                >
                  {`${formatHealth(
                    patient?.provincialInsurance?.cardNumber
                  )}-${patient?.provincialInsurance?.versionCode ?? ""}`}
                </span>
              ) : (
                <span className="font-bold text-gray-800">
                  No Provincial Insurance
                </span>
              )}
            </button>
            {(isHealthCardExpired || !healthCardExpiry) && (
              <NewToolTip
                id={`health_card_tooltip`}
                render={() => (
                  <div className="!bg-white max-w-[460px] text-teal-300 !font-normal text-body-sm p-2">
                    Expired or No Expiry Date
                  </div>
                )}
              />
            )}

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
              show={isShowingProvincialForm}
            >
              <div className="absolute z-50 w-screen max-w-[550px] px-4 pt-2 transform -translate-x-1/2 top-full left-1/2 sm:px-0">
                <div className="overflow-hidden bg-white border-2 border-gray-100 rounded shadow-lg">
                  <div className="flex items-center justify-between px-6 pt-6">
                    <div className="flex-1 text-center">
                      <h3 className="text-body-xl font-semibold text-teal-300">
                        Update Provincial Insurance
                      </h3>
                    </div>
                    <button
                      type="button"
                      className="flex items-center justify-center p-1 transition-all rounded-full max-w-8 cursor-pointer bg-blue-200 hover:bg-REELTeal hover:text-white text-teal-300"
                      onClick={handleProvincialFormClose}
                    >
                      <span className="sr-only">Close panel</span>
                      <XMarkIcon
                        className="w-5 h-5 m-auto"
                        strokeWidth="2"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                  <div className="px-6 pb-6">
                    <div className="border-t border-gray-100 my-4"></div>
                    <div className="grid grid-cols-12 gap-3">
                      <CustomInput
                        name="provincialHealthCard"
                        className="col-span-12"
                        label="Health Card Number"
                        mask="9999-999-999"
                        placeholder="XXX-XXX-XXX"
                        type="text"
                        value={healthCardNumberInput}
                        error={healthCardNumberError}
                        onChange={(e) => {
                          setHealthCardNumberInput(e.target.value);
                          setHealthCardNumberError(
                            isHealthCareError(e.target.value)
                          );
                        }}
                        required
                      />
                      <CustomInput
                        name="provincialVersionCode"
                        className="col-span-6"
                        label="Version Code"
                        placeholder="ER"
                        type="versionCode"
                        value={healthCardVersionInput}
                        error={healthCardVersionError}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          setHealthCardVersionInput(value);
                          setHealthCardVersionError(
                            isVersionCodeError(value)
                          );
                        }}
                        required
                      />
                      <CustomInput
                        name="provincialExpiryDate"
                        className="col-span-6"
                        label="Expiry Date"
                        type="date"
                        value={healthCardExpiryInput}
                        error={healthCardExpiryError}
                        onChange={(value) => {
                          setHealthCardExpiryInput(value);
                          setHealthCardExpiryError(
                            value ? !isValidExpiryDate(value) : false
                          );
                        }}
                      />
                    </div>
                    <div className="flex items-center justify-center gap-5 pt-6">
                      <Button
                        className="min-w-[240px]"
                        text={`Cancel`}
                        type="primary-outline"
                        onClick={handleProvincialFormClose}
                      />
                      <Button
                        className="min-w-[240px]"
                        text="Update & Save"
                        type="primary"
                        onClick={handleProvincialInsuranceUpdate}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Transition>
          </div>
          <p className="flex gap-2">
            <span className="text-gray-700">Insurance :</span>
            {patient?.thirdPartyInsurance.length > 0 && (
              <a
                href=""
                className="ml-1 font-bold text-gray-800 line-clamp-1"
                data-tooltip-id="my-insurance"
              >
                {patient?.thirdPartyInsurance
                  .filter((insurance) => insurance.status === "active")
                  .map(
                    (obj) =>
                      insurancePlanOptions.find(
                        (i) => i.value === obj.insurancePlanName
                      )?.label
                  )
                  .join(", ")}
              </a>
            )}
            {patient?.thirdPartyInsurance?.filter(
              (insurance) => insurance.status == "active"
            ).length >= 2 && (
              <NewToolTip
                id="my-insurance"
                render={() => (
                  <div className="!bg-white max-w-[460px] text-teal-300 !font-normal text-body-sm p-2">
                    {patient?.thirdPartyInsurance
                      .filter((insurance) => insurance.status === "active")
                      .map(
                        (obj) =>
                          insurancePlanOptions.find(
                            (i) => i.value === obj.insurancePlanName
                          )?.label
                      )
                      .join(", ")}
                  </div>
                )}
              />
            )}
            {!(patient?.thirdPartyInsurance.length > 0) && <strong className="text-gray-800"> None </strong>}
          </p>
        </div>
        <div className="flex flex-col flex-1 pl-4 space-y-2 2xl:pl-10 cursor-default">
          <div className="flex gap-2">
          <span
              className={`underline w-full max-w-max ${
                openList ? "text-teal-950" : "text-gray-700"
              } hover:text-teal-950`}
            >
              <button
                onClick={() => setOpenClinicalAlertList(true)}
                className="text-left line-clamp-1 w-full"
              >
                Clinical Alerts :
              </button>
            </span>
            {(() => {
              const visibleAlerts = clinicalAlerts
                .filter((a) => (a?.status ?? "active") !== "inactive")
                .filter((a) => {
                  const rawViewStatus = a?.viewStatus?.value || a?.viewStatus || a?.ViewType || a?.viewType || "";
                  return !String(rawViewStatus).toLowerCase().includes("pharmacist");
                });

              if (!visibleAlerts || visibleAlerts.length === 0) {
                return <strong className="text-gray-800">No Known Alerts</strong>;
              }

              const displayAlerts = visibleAlerts.slice(0, 3);
              const moreCount = visibleAlerts.length - displayAlerts.length;

              return (
                <button
                  type="button"
                  onClick={() => setOpenClinicalAlertList(true)}
                  className="font-bold text-gray-800 text-left whitespace-nowrap overflow-hidden text-ellipsis w-full"
                  data-tooltip-place="bottom"
                  data-tooltip-id="my-clinical-alerts"
                >
                  {displayAlerts
                    .map((alertItem) => (
                      alertItem?.alertType || alertItem?.clinicalAlertType || alertItem?.ClinicalAlertType || ""
                    ))
                    .filter((n) => n && n.toString().trim().length > 0)
                    .join(", ")}
                  {moreCount > 0 && ", ..."}
                  {visibleAlerts.length > 3 && (
                    <NewToolTip
                      id="my-clinical-alerts"
                      render={() => (
                        <div className="!bg-white max-w-[460px] text-teal-300 !font-normal text-body-sm p-2">
                          {visibleAlerts
                            .map((a) => a?.alertType || a?.clinicalAlertType || a?.ClinicalAlertType || "")
                            .filter((n) => n && n.toString().trim().length > 0)
                            .join(", ")}
                        </div>
                      )}
                    />
                  )}
                </button>
              );
            })()}
          </div>
          <p className="flex gap-2">
            <span
              className={`underline w-full max-w-max ${
                openList ? "text-teal-950" : "text-gray-700"
              } hover:text-teal-950`}
            >
              <button
                onClick={() => setOpenList(true)}
                className="text-left line-clamp-1 w-full"
              >
                {" "}
                Allergies :
              </button>
            </span>
            {!(
              allergy !== null &&
              allergy?.allergies?.length > 0 &&
              allergy.allergies.filter(
                (allergyItem) => allergyItem.status == "active"
              ).length > 0
            ) && <strong className="text-gray-800">No Known Allergies</strong>}
            {allergy !== null &&
              allergy?.allergies?.filter(
                (allergyItem) => allergyItem.status == "active"
              ).length > 0 && (
                <div className="font-bold text-gray-800 text-left line-clamp-1 w-full">
                  {allergy.allergies
                    .filter((allergyItem) => allergyItem.status !== "inactive")
                    .map((allergyItem, index) => {
                      const isSevere =
                        severityOptions.find(
                          (option) => option.value === allergyItem.severity
                        )?.label?.toLowerCase() === "severe";

                      return (
                        <span
                          key={allergyItem.id}
                          data-tooltip-place="bottom"
                          data-tooltip-id="my-allergies"
                        >
                          {index > 0 && ", "}
                          <span
                            className={`${
                              isSevere ? "text-red-500" : "text-gray-800"
                            } capitalize`}
                            {...(allergyItem.allergyGenCode === null
                              ? { "data-tooltip-id": "uncoded-allergy" }
                              : {})}
                          >
                            {allergyItem.allergyName}
                          </span>
                        </span>
                      );
                    })}
                </div>
              )}
            {allergy?.allergies?.filter(
              (allergyItem) => allergyItem.status == "active"
            ).length > 3 && (
              <NewToolTip
                id="my-allergies"
                render={() => (
                  <AllergiesToolTip
                    allergies={allergy?.allergies
                      .filter((allergyItem) => allergyItem.status == "active")
                      .map((item) => item.allergyName)
                      .join(", ")}
                  />
                )}
              />
            )}
          </p>
          <NewToolTip id="uncoded-allergy" render={() => <UncodedAllergy />} />
          <div className="flex gap-2">
            <span className="whitespace-nowrap underline text-gray-700">
              <button
                onClick={() => {
                  showMedicalConditions();
                }}
                className=" text-left line-clamp-1  w-full"
              >
                Medical Conditions :
              </button>
            </span>
            {!(
              medicalConditions !== undefined &&
              medicalConditions.filter((val) => val.status === "active")
                .length > 0
            ) && (
              <strong className="text-gray-800">
                No Known Medical Conditions
              </strong>
            )}

            {/* Diabetes, Heart Failure, Heart Attack */}
            {medicalConditions !== undefined &&
              medicalConditions?.filter((cond) => cond.status === "active")
                .length > 0 && (
                <div className="flex-1 ml-1 font-bold text-gray-800 line-clamp-1">
                  {medicalConditions
                    ?.filter((cond) => cond.status === "active")
                    .slice(0, 3)
                    .map((cond, index) => {
                      return (
                        <span
                          key={cond.id}
                          onMouseLeave={() => setShowUnCodedTooltip(false)}
                          data-tooltip-place="bottom"
                          data-tooltip-id="my-condition"
                        >
                          {index > 0 && ", "}
                          <span className="text-gray-800 capitalize">
                            {cond?.conditionName || cond?.name}
                          </span>
                        </span>
                      );
                    })}
                  {medicalConditions?.filter((cond) => cond.status === "active")
                    .length > 3 && <span>...</span>}
                </div>
              )}
            {medicalConditions?.filter((cond) => cond.status === "active")
              ?.length > 3 && (
              <NewToolTip
                id="my-condition"
                render={() => (
                  <MedicalCondition
                    conditions={medicalConditions?.filter(
                      (cond) => cond.status === "active"
                    )}
                  />
                )}
              />
            )}
          </div>
        </div>
        <>
          <a
            onClick={() => {
              if (!checkAnyRunningService()) handleClosePatient();
            }}
            className={`absolute flex p-1 transition-all rounded-full cursor-pointer right-4 top-3 bg-blue-200  text-teal-600 ${
              checkAnyRunningService()
                ? "cursor-not-allowed opacity-50"
                : "hover:bg-teal-600 hover:text-blue-200"
            }`}
            data-tooltip-place="bottom"
            data-tooltip-id="cross"
          >
            <XMarkIcon className="w-4 h-4 m-auto" strokeWidth="2" />
          </a>
          {checkAnyRunningService() && (
            <NewToolTip
              id="cross"
              render={() => (
                <div className="bg-gray-20 p-2 max-w-md flex flex-col">
                  <p className="text-teal-300 text-left text-body-sm font-normal">
                    Please complete or cancel the existing service,
                    <br /> in order to exit the patient.
                  </p>
                </div>
              )}
            />
          )}
        </>
      </div>
      <ListPopup
        title="Allergies"
        open={openList}
        allergies={allergy?.allergies}
        patientId={patient?.id}
        handleClose={() => setOpenList(false)}
      />

      <MedicalConditionListPopup
        title="Medical Conditions"
        open={openMedicalConditionList}
        conditions={medicalConditions}
        patientId={patient?.id}
        handleClose={() => setOpenMedicalConditionList(false)}
      />

      <ClinicalAlertListPopup
        title="Clinical Alerts"
        open={openClinicalAlertList}
        patientId={patient?.id}
        handleClose={() => setOpenClinicalAlertList(false)}
      />

      <ChartUpdatesDialog
        open={openExitServiceDialog}
        title="Medical Conditions Cannot Be Launched"
        titleClassName="!text-[24px]"
        onClose={() => setOpenExitServiceDialog(false)}
        onConfirm={() => {
          setOpenExitServiceDialog(false);
          if (params.serviceId) {
            navigate(`/patient/${params.id}/clinical-services/${params.serviceId}`);
          } else {
            // Find the running Minor Ailment service
            const runningService = minorAilments?.find(
              (svc) => svc.status === "inProgress"
            );
            if (runningService) {
              navigate(`/patient/${params.id}/clinical-services/${runningService.id}`);
            }
          }
        }}
      />
    </div>
  );
};

export default Overview;
